import type {
  ManifestDetail,
  PartyType,
  Cargo,
  Container,
  HouseBillOperation,
  BillStatus,
} from '@/typings/bill'
import {
  MovementType,
  FiveCodeType,
  BillType,
  TemperatureUnit,
  ToOrder,
} from '@/const'
import { v4 as uuidv4 } from 'uuid'

/** 创建舱单风险状态 */
export const createBillRiskStatus = () => {
  return {
    id: '',
    houseBillId: '',
    riskStatus: '',
    riskStatusAt: '',
    productCode: '',
    createTime: '',
  } as BillStatus
}

/** 舱单最新状态 */
export const createBillOperation = () => {
  return {
    id: '',
    houseBillId: '',
    operatorId: '',
    operatorAccount: '',
    operatorNickname: '',
    operationAt: '',
    ackStatusAt: '',
    ackStatus: '',
    remark: '',
    cyberReplyContentId: '',
    cyberSendContentId: '',
    messageReferenceNo: '',
    ackError: '',
    ackErrorEn: '',
    enterpriseId: '',
    enterpriseName: '',
    type: '',
    typeName: '',
    ackName: '',
    ackAt: '',
    ackErrorCode: '',
    ackBadData: '',
    source: '',
    productCode: '',
    createTime: '',
    hgjProxyUsername: '',
  } as HouseBillOperation
}

/** 创建货物 */
export const createDefaultCargo = (groupId: string): Cargo => {
  return {
    groupId,
    id: '',
    containerNo: '',
    hsCode: '',
    packages: '',
    packageUnit: '',
    grossWeight: '',
    undgNumber: '',
    cargoDescription: '',
    markNumbers: '',
    productCode: '',
    createTime: 0,
  }
}

/** 创建集装箱 */
export const createDefaultContainer = (): Container => {
  const groupId = uuidv4()
  return {
    groupId,
    id: '',
    containerNo: '',
    sealNos: '',
    containerTypeSize: '',
    emptyStatus: '',
    grossWeight: '',
    temperature: '',
    temperatureUnit: TemperatureUnit.CELSIUS,
    productCode: '',
    createTime: 0,
    cargos: [createDefaultCargo(groupId)],
  }
}

/** 创建联系人信息 */
export const createContactInfo = (partyType: PartyType) => {
  return {
    partyName: '',
    partyAddress: '',
    partyCountryCode: '',
    partyCity: '',
    partyType,
    id: '',
  }
}

/** 创建主分单共用的基础数据 */
export const createBillModel = (): ManifestDetail => {
  const container = createDefaultContainer()
  return {
    userRemark: '',
    success: false,
    id: '',
    billType: ToOrder.NON,
    delegatorId: '',
    movementType: MovementType.IMPORT,
    currentLayerHouseCount: '',
    consolidatedIndicator: false,
    enterpriseId: 0,
    enterpriseName: '',
    userId: 0,
    updatedAt: 0,
    freightForwarderPartyName: '',
    deliveryAgentCompanyName: '',
    portOfLoadingCode: '',
    portOfLoading: '',
    portOfLoadingEn: '',
    portOfDischargeCode: '',
    portOfDischarge: '',
    portOfDischargeEn: '',
    carrierScac: '',
    carrierName: '',
    carrierNameEn: '',
    mpciPartyId: '',
    placeOfDeliveryCode: '',
    placeOfDeliveryCountryCode: '',
    placeOfDeliveryCity: '',
    placeOfDelivery: '',
    placeOfDeliveryEn: '',
    placeOfDeliveryType: FiveCodeType.UN_CODE,
    placeOfReceiptCode: '',
    placeOfReceiptCountryCode: '',
    placeOfReceiptCity: '',
    placeOfReceipt: '',
    placeOfReceiptEn: '',
    placeOfReceiptType: FiveCodeType.UN_CODE,
    previousBillType: BillType.EMPTY,
    previousHouseBillNo: '',
    previousMpciPartyId: '',
    nextMpciPartyId: '',
    houseBillNo: '',
    houseBillNoReverse: '',
    createTime: 0,
    houseBillOperationId: '',
    parties: [],
    containers: [container],
    houseBillOperation: createBillOperation(),
    billStatus: createBillRiskStatus(),
  }
}

// 将嵌套的 cargos 平铺并从 containers 中移除，用于传给后端
export const flatCargos = (detail: ManifestDetail) => {
  const flattenedCargos: any[] = []

  // 收集所有容器中的 cargos，同时设置 containerNo
  detail.containers.forEach((container) => {
    if (container.cargos && container.cargos.length > 0) {
      container.cargos.forEach((cargo) => {
        // 设置 cargo 的 containerNo 为当前容器的 containerNo
        cargo.containerNo = container.containerNo
      })
      flattenedCargos.push(...container.cargos)
    }
  })

  // 创建新的数据结构，移除 containers 中的 cargos 字段
  const result = {
    ...detail,
    cargos: flattenedCargos,
    containers: detail.containers.map((container) => {
      const { cargos, ...containerWithoutCargos } = container
      return containerWithoutCargos
    }),
  }

  return result
}
